import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Save, Trash2, Wrench, Pencil } from "lucide-react";
import MultiImageUpload from "@/components/MultiImageUpload";
import MachineTakeStats from "@/components/MachineTakeStats";

const KIND_OPTIONS = ["Service", "Spare parts", "Consumables", "Repair", "Call-out"];

const emptyForm = (presetKind, presetNotes) => ({
  serviced_at: new Date().toISOString().slice(0, 10),
  kind: presetKind || "Service",
  performed_by_user_id: "",
  performed_by_tech_id: "",
  performed_by_text: presetNotes ? "" : "",
  notes: presetNotes || "",
  cost: "",
  hours_reading: "",
  image_paths: [],
});

/**
 * Manager-only — log a new service event for a machine, and view past events.
 * Each entry: date, kind (Service/Spare parts/Repair), who performed it,
 * notes, cost, optional service-sheet photos. Saving rolls
 * machine.last_serviced_at forward + clears any needs_service flags.
 */
const MachineServiceLogDialog = ({ open, onClose, machine, users, onSaved, presetKind, presetNotes }) => {
  const [history, setHistory] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editingKindFor, setEditingKindFor] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null); // full-row edit mode
  const [editForm, setEditForm] = useState(null);
  const [form, setForm] = useState(emptyForm(presetKind, presetNotes));

  const load = async () => {
    if (!machine?.id) return;
    try {
      const { data } = await api.get(`/machines/${machine.id}/services`);
      setHistory(data || []);
    } catch (_) {
      /* transient */
    }
  };

  useEffect(() => {
    if (open && machine) {
      setForm({
        serviced_at: new Date().toISOString().slice(0, 10),
        kind: presetKind || "Service",
        performed_by_user_id: "",
        performed_by_tech_id: "",
        performed_by_text: presetNotes ? "" : "",
        notes: presetNotes || "",
        cost: "",
        hours_reading: "",
        image_paths: [],
      });
      setEditingKindFor(null);
      setEditingRowId(null);
      setEditForm(null);
      load();
      // Load directory technicians for the dropdown.
      api
        .get("/technicians")
        .then(({ data }) => setTechnicians(data || []))
        .catch(() => setTechnicians([]));
    }
  }, [open, machine?.id, presetKind, presetNotes]);

  if (!open || !machine) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/machines/${machine.id}/services`, {
        ...form,
        cost: form.cost === "" ? null : Number(form.cost),
        hours_reading: form.hours_reading === "" ? null : Number(form.hours_reading),
        performed_by_user_id: form.performed_by_user_id || null,
        performed_by_tech_id: form.performed_by_tech_id || null,
        performed_by_text: form.performed_by_text || null,
      });
      toast.success("Service logged");
      await load();
      onSaved?.();
      setForm({
        serviced_at: new Date().toISOString().slice(0, 10),
        kind: "Service",
        performed_by_user_id: "",
        performed_by_tech_id: "",
        performed_by_text: "",
        notes: "",
        cost: "",
        hours_reading: "",
        image_paths: [],
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const removeEntry = async (sid) => {
    if (!window.confirm("Delete this service entry?")) return;
    try {
      await api.delete(`/machines/${machine.id}/services/${sid}`);
      toast.success("Removed");
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const retagKind = async (sid, kind) => {
    try {
      await api.put(`/machines/${machine.id}/services/${sid}`, { kind });
      toast.success(`Re-tagged as ${kind}`);
      setEditingKindFor(null);
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not re-tag");
    }
  };

  // Open the inline "full edit" panel on a past-service row so managers can
  // attach the service-sheet photos after the fact or fix a typo.
  const startEdit = (h) => {
    setEditingKindFor(null);
    setEditingRowId(h.id);
    setEditForm({
      serviced_at: h.serviced_at || new Date().toISOString().slice(0, 10),
      kind: h.kind || "Service",
      performed_by_user_id: h.performed_by_user_id || "",
      performed_by_tech_id: h.performed_by_tech_id || "",
      performed_by_text: h.performed_by_text || "",
      notes: h.notes || "",
      cost: h.cost == null ? "" : String(h.cost),
      hours_reading: h.hours_reading == null ? "" : String(h.hours_reading),
      image_paths: h.image_paths || [],
    });
  };

  const setEdit = (k, v) => setEditForm((f) => ({ ...f, [k]: v }));

  const saveEdit = async () => {
    if (!editingRowId || !editForm) return;
    setBusy(true);
    try {
      await api.put(`/machines/${machine.id}/services/${editingRowId}`, {
        ...editForm,
        cost: editForm.cost === "" ? null : Number(editForm.cost),
        hours_reading:
          editForm.hours_reading === "" ? null : Number(editForm.hours_reading),
        performed_by_user_id: editForm.performed_by_user_id || null,
        performed_by_tech_id: editForm.performed_by_tech_id || null,
        performed_by_text: editForm.performed_by_text || null,
      });
      toast.success("Service updated");
      setEditingRowId(null);
      setEditForm(null);
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const kindBadgeClass = (k) =>
    k === "Spare parts"
      ? "bg-[#E8F2FF] text-[#0A3A7A] border-[#0A3A7A]"
      : k === "Consumables"
        ? "bg-[#E6F4EA] text-[#1F6F3E] border-[#1F6F3E]"
        : k === "Repair"
          ? "bg-[#FCE4E4] text-[#8A1F1F] border-[#8A1F1F]"
          : k === "Call-out"
            ? "bg-[#FEE0CB] text-[#7A2E00] border-[#7A2E00]"
            : "bg-[#FFF7DB] text-[#5B4500] border-[#7A4A00]";

  const userName = (uid) => {
    if (!uid) return null;
    const u = users.find((x) => x.id === uid);
    return u ? u.name : null;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="machine-service-dialog"
      onClick={onClose}
    >
      <div
        className="nb-card w-[min(820px,95vw)] max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white px-5 py-4 border-b-2 border-black flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Machinery</div>
            <h3 className="font-display text-2xl flex items-center gap-2">
              <Wrench size={20} strokeWidth={2.5} /> Service: {machine.name}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="nb-btn bg-white p-2"
            data-testid="machine-service-close"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <MachineTakeStats machineId={machine.id} />

        <div className="p-5 grid gap-4 sm:grid-cols-2">
          <label>
            <div className="label-eyebrow mb-1">Date *</div>
            <input
              type="date"
              data-testid="service-date-input"
              className="nb-input"
              value={form.serviced_at}
              onChange={(e) => set("serviced_at", e.target.value)}
            />
          </label>

          <label>
            <div className="label-eyebrow mb-1">Performed by (team member)</div>
            <select
              data-testid="service-by-user-select"
              className="nb-input"
              value={form.performed_by_user_id}
              onChange={(e) => set("performed_by_user_id", e.target.value)}
            >
              <option value="">— None —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            <div className="label-eyebrow mb-1">Or external technician</div>
            <select
              data-testid="service-by-tech-select"
              className="nb-input"
              value={form.performed_by_tech_id || ""}
              onChange={(e) => {
                const tid = e.target.value;
                set("performed_by_tech_id", tid);
                // Clear free-text override when picking from directory so the
                // report + history render the tech's canonical name.
                if (tid) set("performed_by_text", "");
              }}
            >
              <option value="">— None —</option>
              {(technicians || [])
                .filter((t) => t.is_active !== false)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                    {t.supplier_name ? ` · ${t.supplier_name}` : ""}
                  </option>
                ))}
            </select>
          </label>

          <label className="sm:col-span-2">
            <div className="label-eyebrow mb-1">
              Or free-text (for a one-off contractor not in the directory)
            </div>
            <input
              data-testid="service-by-text-input"
              className="nb-input"
              value={form.performed_by_text}
              onChange={(e) => set("performed_by_text", e.target.value)}
              placeholder="e.g. ACME Service Co — Tom"
              disabled={!!form.performed_by_tech_id}
            />
          </label>

          <label>
            <div className="label-eyebrow mb-1">Cost ($)</div>
            <input
              type="number"
              step="0.01"
              data-testid="service-cost-input"
              className="nb-input font-mono-num"
              value={form.cost}
              onChange={(e) => set("cost", e.target.value)}
              placeholder="0.00"
            />
          </label>

          <label>
            <div className="label-eyebrow mb-1">Kind *</div>
            <select
              data-testid="service-kind-select"
              className="nb-input"
              value={form.kind}
              onChange={(e) => set("kind", e.target.value)}
            >
              {KIND_OPTIONS.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </label>

          <label>
            <div className="label-eyebrow mb-1">Hour-meter reading</div>
            <input
              type="number"
              min="0"
              step="any"
              data-testid="service-hours-input"
              className="nb-input font-mono-num"
              value={form.hours_reading}
              onChange={(e) => set("hours_reading", e.target.value)}
              placeholder={
                machine.hours != null
                  ? `previous: ${machine.hours}`
                  : "e.g. 4582.5"
              }
            />
          </label>

          <label className="sm:col-span-2">
            <div className="label-eyebrow mb-1">Notes</div>
            <textarea
              data-testid="service-notes-input"
              className="nb-input min-h-[60px]"
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="What was done, parts replaced, etc."
            />
          </label>

          <div className="sm:col-span-2">
            <div className="label-eyebrow mb-1">Service sheet / photos</div>
            <MultiImageUpload
              paths={form.image_paths || []}
              onChange={(paths) => set("image_paths", paths)}
            />
          </div>

          <div className="sm:col-span-2 flex justify-end">
            <button
              data-testid="service-save-btn"
              onClick={submit}
              disabled={busy || !form.serviced_at}
              className="nb-btn nb-btn-primary px-4 py-2 flex items-center gap-1"
            >
              <Save size={14} strokeWidth={2.5} /> Log service
            </button>
          </div>

          <div className="sm:col-span-2 border-t-2 border-black/10 pt-4">
            <div className="label-eyebrow mb-2">Past services ({history.length})</div>
            {history.length === 0 ? (
              <div className="text-sm text-[#607087] italic">
                No services logged yet.
              </div>
            ) : (
              <ul className="flex flex-col gap-2">
                {history.map((h) => {
                  const kind = h.kind || "Service";
                  const editing = editingKindFor === h.id;
                  const rowEditing = editingRowId === h.id;
                  return (
                    <li
                      key={h.id}
                      data-testid={`service-row-${h.id}`}
                      className="border-2 border-black rounded-xl bg-white"
                    >
                      <div className="p-3 flex gap-3">
                        <div className="font-mono-num font-bold text-base shrink-0">
                          {h.serviced_at}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {editing ? (
                              <select
                                data-testid={`service-kind-edit-${h.id}`}
                                className="nb-input px-2 py-1 text-xs"
                                value={kind}
                                autoFocus
                                onChange={(e) => retagKind(h.id, e.target.value)}
                                onBlur={() => setEditingKindFor(null)}
                              >
                                {KIND_OPTIONS.map((k) => (
                                  <option key={k} value={k}>
                                    {k}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <button
                                data-testid={`service-kind-badge-${h.id}`}
                                onClick={() => setEditingKindFor(h.id)}
                                className={`inline-flex items-center gap-1 border-2 rounded-md px-2 py-0.5 text-xs font-semibold ${kindBadgeClass(kind)}`}
                                title="Re-tag"
                              >
                                {kind}
                                <Pencil size={10} strokeWidth={2.5} />
                              </button>
                            )}
                            <div className="text-sm">
                              {userName(h.performed_by_user_id) ||
                                h.performed_by_text ||
                                h.performed_by_name ||
                                "—"}
                              {h.supplier_name && (
                                <span className="text-[10px] text-[#607087] ml-1">
                                  · {h.supplier_name}
                                </span>
                              )}
                              {h.cost != null && (
                                <span className="font-mono-num text-[#607087] ml-2">
                                  ${Number(h.cost).toFixed(2)}
                                </span>
                              )}
                            </div>
                          </div>
                          {h.notes && (
                            <div className="text-xs text-[#607087] mt-1 whitespace-pre-wrap">
                              {h.notes}
                            </div>
                          )}
                          {h.image_paths?.length > 0 && (
                            <div className="text-xs text-[#607087] mt-1">
                              {h.image_paths.length} photo{h.image_paths.length === 1 ? "" : "s"} attached
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-1 shrink-0">
                          <button
                            data-testid={`service-edit-${h.id}`}
                            onClick={() =>
                              rowEditing
                                ? (setEditingRowId(null), setEditForm(null))
                                : startEdit(h)
                            }
                            className={`nb-btn p-2 ${rowEditing ? "nb-btn-dark" : "bg-white"}`}
                            aria-label="edit"
                            title={rowEditing ? "Cancel edit" : "Edit entry & attach photos"}
                          >
                            <Pencil size={14} strokeWidth={2.5} />
                          </button>
                          <button
                            data-testid={`service-delete-${h.id}`}
                            onClick={() => removeEntry(h.id)}
                            className="nb-btn nb-btn-danger p-2"
                            aria-label="delete"
                          >
                            <Trash2 size={14} strokeWidth={2.5} />
                          </button>
                        </div>
                      </div>

                      {rowEditing && editForm && (
                        <div
                          data-testid={`service-edit-panel-${h.id}`}
                          className="border-t-2 border-black/10 p-3 grid gap-3 sm:grid-cols-2 bg-[#F8FAFC]"
                        >
                          <label>
                            <div className="label-eyebrow mb-1">Date *</div>
                            <input
                              type="date"
                              data-testid={`service-edit-date-${h.id}`}
                              className="nb-input"
                              value={editForm.serviced_at}
                              onChange={(e) => setEdit("serviced_at", e.target.value)}
                            />
                          </label>
                          <label>
                            <div className="label-eyebrow mb-1">Kind *</div>
                            <select
                              data-testid={`service-edit-kind-${h.id}`}
                              className="nb-input"
                              value={editForm.kind}
                              onChange={(e) => setEdit("kind", e.target.value)}
                            >
                              {KIND_OPTIONS.map((k) => (
                                <option key={k} value={k}>{k}</option>
                              ))}
                            </select>
                          </label>
                          <label>
                            <div className="label-eyebrow mb-1">Performed by (team)</div>
                            <select
                              data-testid={`service-edit-user-${h.id}`}
                              className="nb-input"
                              value={editForm.performed_by_user_id}
                              onChange={(e) => setEdit("performed_by_user_id", e.target.value)}
                            >
                              <option value="">— None —</option>
                              {users.map((u) => (
                                <option key={u.id} value={u.id}>{u.name}</option>
                              ))}
                            </select>
                          </label>
                          <label>
                            <div className="label-eyebrow mb-1">External technician</div>
                            <select
                              data-testid={`service-edit-tech-${h.id}`}
                              className="nb-input"
                              value={editForm.performed_by_tech_id || ""}
                              onChange={(e) => {
                                const tid = e.target.value;
                                setEdit("performed_by_tech_id", tid);
                                if (tid) setEdit("performed_by_text", "");
                              }}
                            >
                              <option value="">— None —</option>
                              {(technicians || [])
                                .filter((t) => t.is_active !== false)
                                .map((t) => (
                                  <option key={t.id} value={t.id}>
                                    {t.name}
                                    {t.supplier_name ? ` · ${t.supplier_name}` : ""}
                                  </option>
                                ))}
                            </select>
                          </label>
                          <label className="sm:col-span-2">
                            <div className="label-eyebrow mb-1">Or free-text contractor</div>
                            <input
                              data-testid={`service-edit-text-${h.id}`}
                              className="nb-input"
                              value={editForm.performed_by_text}
                              onChange={(e) => setEdit("performed_by_text", e.target.value)}
                              placeholder="e.g. ACME Service Co — Tom"
                              disabled={!!editForm.performed_by_tech_id}
                            />
                          </label>
                          <label>
                            <div className="label-eyebrow mb-1">Cost ($)</div>
                            <input
                              type="number"
                              step="0.01"
                              data-testid={`service-edit-cost-${h.id}`}
                              className="nb-input font-mono-num"
                              value={editForm.cost}
                              onChange={(e) => setEdit("cost", e.target.value)}
                              placeholder="0.00"
                            />
                          </label>
                          <label>
                            <div className="label-eyebrow mb-1">Hour-meter reading</div>
                            <input
                              type="number"
                              min="0"
                              step="any"
                              data-testid={`service-edit-hours-${h.id}`}
                              className="nb-input font-mono-num"
                              value={editForm.hours_reading}
                              onChange={(e) => setEdit("hours_reading", e.target.value)}
                            />
                          </label>
                          <label className="sm:col-span-2">
                            <div className="label-eyebrow mb-1">Notes</div>
                            <textarea
                              data-testid={`service-edit-notes-${h.id}`}
                              className="nb-input min-h-[60px]"
                              value={editForm.notes}
                              onChange={(e) => setEdit("notes", e.target.value)}
                            />
                          </label>
                          <div className="sm:col-span-2">
                            <div className="label-eyebrow mb-1">
                              Service sheet / photos
                              <span className="text-[10px] text-[#607087] normal-case ml-2">
                                — snap the technician's report even after the service
                              </span>
                            </div>
                            <MultiImageUpload
                              paths={editForm.image_paths || []}
                              onChange={(paths) => setEdit("image_paths", paths)}
                            />
                          </div>
                          <div className="sm:col-span-2 flex justify-end gap-2">
                            <button
                              data-testid={`service-edit-cancel-${h.id}`}
                              onClick={() => {
                                setEditingRowId(null);
                                setEditForm(null);
                              }}
                              className="nb-btn bg-white px-3 py-2 text-sm"
                            >
                              Cancel
                            </button>
                            <button
                              data-testid={`service-edit-save-${h.id}`}
                              onClick={saveEdit}
                              disabled={busy || !editForm.serviced_at}
                              className="nb-btn nb-btn-primary px-4 py-2 flex items-center gap-1"
                            >
                              <Save size={14} strokeWidth={2.5} /> Save changes
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MachineServiceLogDialog;
